import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/**
 * What this device keeps for every person who signs in on it: one AES-GCM
 * key each (non-extractable: the browser can use it, nobody can read it
 * out), and who is signed in now. The key never leaves the device.
 */
interface DeviceSchema extends DBSchema {
  keys: { key: string; value: CryptoKey };
  meta: { key: string; value: string | null };
}

let device: Promise<IDBPDatabase<DeviceSchema>> | null = null;

function deviceDb() {
  device ??= openDB<DeviceSchema>('wr-portal-device', 1, {
    upgrade(db) {
      db.createObjectStore('keys');
      db.createObjectStore('meta');
    },
  });
  return device;
}

/** The person's key on this device, created at their first sign-in here. */
export async function userKey(userId: string): Promise<CryptoKey> {
  const db = await deviceDb();
  const existing = await db.get('keys', userId);
  if (existing) return existing;
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, [
    'encrypt',
    'decrypt',
  ]);
  await db.put('keys', key, userId);
  return key;
}

/** Who is signed in on this device (null after signing out). */
export async function currentUserId(): Promise<string | null> {
  return (await (await deviceDb()).get('meta', 'currentUser')) ?? null;
}

export async function setCurrentUserId(userId: string | null): Promise<void> {
  await (await deviceDb()).put('meta', userId, 'currentUser');
}

export interface Sealed {
  iv: Uint8Array<ArrayBuffer>;
  data: ArrayBuffer;
}

export async function seal(key: CryptoKey, value: unknown): Promise<Sealed> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(JSON.stringify(value)),
  );
  return { iv, data };
}

export async function unseal<T>(key: CryptoKey, sealed: Sealed): Promise<T> {
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: sealed.iv }, key, sealed.data);
  return JSON.parse(new TextDecoder().decode(plain)) as T;
}

/** A file (a scanned card), encrypted as it is, without turning it into text. */
export async function sealBytes(key: CryptoKey, bytes: ArrayBuffer): Promise<Sealed> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  return { iv, data: await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, bytes) };
}

export async function unsealBytes(key: CryptoKey, sealed: Sealed): Promise<ArrayBuffer> {
  return crypto.subtle.decrypt({ name: 'AES-GCM', iv: sealed.iv }, key, sealed.data);
}

/** For tests: forget the open connection. */
export function resetDeviceForTests(): void {
  device = null;
}
